<script setup>
// PantallaTatami.vue
// Pantalla TV de un solo tatami — solo lectura, sin token.
// Ruta sugerida: /evento/tatami/:tatamiId/pantalla

import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRoute } from "vue-router";
import { connectPublico, closePublico } from "@/utils/ws_peticiones_evento";

const route = useRoute();
const tatamiId = Number(route.params.tatamiId || route.params.id || 1);

// ── Estado ────────────────────────────────────────────────────────────────────
const pelea = ref(null);
const marcador = ref(null);
const ganador = ref(null);
let ganadorTimer = null;

// ── Cronómetro ────────────────────────────────────────────────────────────────
const DURACION = ref(300);
const tiempoLocal = ref(0);
const cronActivo = ref(false);
let tickInterval = null;
let lastSyncTime = null;
let lastSyncSeg = 0;

// ── Computed ──────────────────────────────────────────────────────────────────
const tiempoDisplay = computed(() => {
  const r = Math.max(0, DURACION.value - tiempoLocal.value);
  return `${String(Math.floor(r / 60)).padStart(2, "0")}:${String(
    Math.floor(r % 60)
  ).padStart(2, "0")}`;
});
const porcentaje = computed(() =>
  Math.min(100, (tiempoLocal.value / DURACION.value) * 100)
);
const tiempoAgotado = computed(() => pelea.value?.estado === "tiempo_agotado");

// ── Lifecycle ─────────────────────────────────────────────────────────────────
onMounted(() => {
  connectPublico({ onMessage: manejar });
});
onUnmounted(() => {
  closePublico();
  detenerTick();
  clearTimeout(ganadorTimer);
});

// ── WS handler ────────────────────────────────────────────────────────────────
function manejar(msg) {
  if (msg.type === "estado_inicial") {
    const tatamis = msg.data?.tatamis || [];
    const miTatami = tatamis.find((t) => Number(t.tatami_id) === tatamiId);
    if (!miTatami) return;
    const lista = miTatami.peleas || [];
    const activa =
      lista.find((p) => ["en_curso", "pausada"].includes(p.estado)) ||
      lista.find((p) => p.estado === "pendiente") ||
      null;
    if (activa) {
      pelea.value = activa;
      marcador.value = activa.marcador || null;
      if (activa.duracion_seg) DURACION.value = activa.duracion_seg;
      if (marcador.value) sincronizar(marcador.value);
    }
    return;
  }

  if (msg.type === "marcador_update") {
    if (Number(msg.tatami_id) !== tatamiId) return;

    if (msg.duracion_seg) DURACION.value = msg.duracion_seg;

    if (msg.estado_pelea === "finalizada" && msg.ganador) {
      const lado = typeof msg.ganador === "string" ? msg.ganador : null;
      const nombre =
        lado === "rojo" ? msg.rojo?.nombre ?? null : msg.azul?.nombre ?? null;
      if (nombre) mostrarGanador(nombre, lado);
    }

    pelea.value = {
      pelea_id: msg.pelea_id,
      ronda: msg.ronda,
      estado: msg.estado_pelea,
      rojo: msg.rojo || pelea.value?.rojo || null,
      azul: msg.azul || pelea.value?.azul || null,
    };

    const m = msg.marcador || msg;
    marcador.value = {
      puntos_rojo: m.puntos_rojo ?? marcador.value?.puntos_rojo ?? 0,
      puntos_azul: m.puntos_azul ?? marcador.value?.puntos_azul ?? 0,
      ventajas_rojo: m.ventajas_rojo ?? marcador.value?.ventajas_rojo ?? 0,
      ventajas_azul: m.ventajas_azul ?? marcador.value?.ventajas_azul ?? 0,
      amonestaciones_rojo:
        m.amonestaciones_rojo ?? marcador.value?.amonestaciones_rojo ?? 0,
      amonestaciones_azul:
        m.amonestaciones_azul ?? marcador.value?.amonestaciones_azul ?? 0,
      tiempo_transcurrido: m.tiempo_transcurrido,
      cronometro_activo: m.cronometro_activo,
      server_timestamp: m.server_timestamp ?? msg.server_timestamp,
    };
    sincronizar(marcador.value);
  }
}

// ── Cronómetro local ──────────────────────────────────────────────────────────
function sincronizar(m) {
  if (!m) return;
  let base = m.tiempo_transcurrido || 0;
  if (m.server_timestamp && m.cronometro_activo) {
    base += Math.max(0, (Date.now() - new Date(m.server_timestamp).getTime()) / 1000);
  }
  lastSyncSeg = base;
  lastSyncTime = Date.now();
  tiempoLocal.value = Math.min(base, DURACION.value);
  const era = cronActivo.value;
  cronActivo.value = !!m.cronometro_activo;
  if (m.cronometro_activo && !era) iniciarTick();
  if (!m.cronometro_activo) detenerTick();
}

function iniciarTick() {
  detenerTick();
  tickInterval = setInterval(() => {
    if (!cronActivo.value || !lastSyncTime) return;
    tiempoLocal.value = Math.min(
      lastSyncSeg + (Date.now() - lastSyncTime) / 1000,
      DURACION.value
    );
  }, 250);
}

function detenerTick() {
  if (tickInterval) {
    clearInterval(tickInterval);
    tickInterval = null;
  }
}

function mostrarGanador(nombre, lado) {
  ganador.value = { nombre, lado };
  clearTimeout(ganadorTimer);
  ganadorTimer = setTimeout(() => {
    ganador.value = null;
  }, 7000);
}
</script>

<template>
  <div class="pantalla">
    <!-- Header -->
    <div class="header">
      <span class="tatami-label">TATAMI {{ tatamiId }}</span>
      <span v-if="pelea" class="estado-chip" :class="pelea.estado">
        {{
          {
            pendiente: "PENDIENTE",
            en_curso: "EN CURSO",
            pausada: "PAUSADA",
            tiempo_agotado: "TIEMPO AGOTADO",
            finalizada: "FINALIZADA",
          }[pelea.estado]
        }}
      </span>
      <span v-if="pelea" class="ronda-chip">RONDA {{ pelea.ronda }}</span>
    </div>

    <!-- Sin pelea -->
    <div v-if="!pelea" class="sin-pelea">
      <span>⚔️</span>
      <p>Sin combate activo</p>
    </div>

    <!-- Scoreboard -->
    <div v-else class="scoreboard">
      <!-- ROJO -->
      <div class="fighter rojo">
        <div class="fighter-info">
          <div class="fighter-name">{{ pelea.rojo?.nombre || "BYE" }}</div>
          <div class="fighter-team">{{ pelea.rojo?.equipo || "" }}</div>
        </div>
        <div class="scores">
          <div class="score-block pts">
            <div class="score-val">{{ marcador?.puntos_rojo ?? 0 }}</div>
            <div class="score-lbl">PTS</div>
          </div>
          <div class="score-block adv">
            <div class="score-val">{{ marcador?.ventajas_rojo ?? 0 }}</div>
            <div class="score-lbl">ADV</div>
          </div>
          <div class="score-block pen">
            <div class="score-val">{{ marcador?.amonestaciones_rojo ?? 0 }}</div>
            <div class="score-lbl">PEN</div>
          </div>
        </div>
      </div>

      <!-- AZUL -->
      <div class="fighter azul">
        <div class="fighter-info">
          <div class="fighter-name">{{ pelea.azul?.nombre || "BYE" }}</div>
          <div class="fighter-team">{{ pelea.azul?.equipo || "" }}</div>
        </div>
        <div class="scores">
          <div class="score-block pts">
            <div class="score-val">{{ marcador?.puntos_azul ?? 0 }}</div>
            <div class="score-lbl">PTS</div>
          </div>
          <div class="score-block adv">
            <div class="score-val">{{ marcador?.ventajas_azul ?? 0 }}</div>
            <div class="score-lbl">ADV</div>
          </div>
          <div class="score-block pen">
            <div class="score-val">{{ marcador?.amonestaciones_azul ?? 0 }}</div>
            <div class="score-lbl">PEN</div>
          </div>
        </div>
      </div>

      <!-- Timer -->
      <div class="timer-bar">
        <div
          class="timer-display"
          :class="{
            warning: porcentaje > 70 && porcentaje <= 90,
            danger: porcentaje > 90 || tiempoAgotado,
          }"
        >
          {{ tiempoDisplay }}
        </div>
        <div class="timer-track">
          <div
            class="timer-fill"
            :class="{ warning: porcentaje > 70, danger: porcentaje > 90 }"
            :style="{ width: porcentaje + '%' }"
          ></div>
        </div>
      </div>
    </div>

    <!-- Overlay ganador -->
    <Transition name="winner-pop">
      <div v-if="ganador" class="winner-overlay" :class="ganador.lado">
        <div class="winner-glow"></div>
        <div class="winner-inner">
          <div class="winner-trophy">🏆</div>
          <div class="winner-label">GANADOR</div>
          <div class="winner-name">{{ ganador.nombre }}</div>
        </div>
        <div class="confetti-wrap">
          <span v-for="i in 14" :key="i" class="conf" :style="{ '--i': i }"></span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Oswald:wght@400;600;700&family=Orbitron:wght@700&display=swap");

.pantalla {
  width: 100vw;
  height: 100vh;
  background: #0a0a0a;
  display: flex;
  flex-direction: column;
  font-family: "Oswald", sans-serif;
  color: #fff;
  overflow: hidden;
  position: relative;
}

/* Header */
.header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 28px;
  background: #111;
  border-bottom: 3px solid #f3bc2c;
  flex-shrink: 0;
}
.tatami-label {
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 4px;
  color: #f3bc2c;
}
.estado-chip {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 2px;
  padding: 4px 14px;
  border-radius: 20px;
  border: 1.5px solid currentColor;
}
.estado-chip.pendiente {
  color: #60a5fa;
  background: rgba(59, 130, 246, 0.1);
}
.estado-chip.en_curso {
  color: #4ade80;
  background: rgba(34, 197, 94, 0.1);
}
.estado-chip.pausada {
  color: #fbbf24;
  background: rgba(245, 158, 11, 0.1);
}
.estado-chip.tiempo_agotado {
  color: #f87171;
  background: rgba(239, 68, 68, 0.1);
}
.estado-chip.finalizada {
  color: #9ca3af;
  background: rgba(107, 114, 128, 0.1);
}
.ronda-chip {
  margin-left: auto;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 3px;
  color: rgba(255, 255, 255, 0.4);
}

/* Sin pelea */
.sin-pelea {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: rgba(255, 255, 255, 0.2);
  font-size: 1.4rem;
  letter-spacing: 2px;
}
.sin-pelea span {
  font-size: 4rem;
}

/* Scoreboard */
.scoreboard {
  flex: 1;
  display: flex;
  flex-direction: column;
}

/* Fighter rows */
.fighter {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 32px;
  gap: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.fighter.rojo {
  background: linear-gradient(
    90deg,
    rgba(220, 38, 38, 0.7) 0%,
    rgba(220, 38, 38, 0.2) 60%,
    transparent 100%
  );
  border-left: 8px solid #ef4444;
}
.fighter.azul {
  background: linear-gradient(
    90deg,
    rgba(37, 99, 235, 0.7) 0%,
    rgba(37, 99, 235, 0.2) 60%,
    transparent 100%
  );
  border-left: 8px solid #3b82f6;
}

.fighter-info {
  flex: 1;
  min-width: 0;
}

.fighter-name {
  font-size: clamp(2rem, 4.5vw, 4rem);
  font-weight: 700;
  text-transform: uppercase;
  line-height: 1;
  letter-spacing: 1px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fighter-team {
  font-size: clamp(0.9rem, 1.8vw, 1.4rem);
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-top: 4px;
}

/* Score blocks */
.scores {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.score-block {
  width: clamp(65px, 9vw, 120px);
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  gap: 2px;
}
.score-block.pts {
  background: #2563eb;
  border: 2px solid #60a5fa;
}
.score-block.adv {
  background: #d97706;
  border: 2px solid #fbbf24;
}
.score-block.pen {
  background: #dc2626;
  border: 2px solid #f87171;
}

.score-val {
  font-size: clamp(2rem, 4.5vw, 4.5rem);
  font-weight: 700;
  line-height: 1;
  color: #fff;
}
.score-lbl {
  font-size: clamp(0.55rem, 1vw, 0.85rem);
  font-weight: 700;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.7);
}

/* Timer */
.timer-bar {
  flex-shrink: 0;
  background: #111;
  border-top: 2px solid #f3bc2c;
  padding: 16px 32px;
  display: flex;
  align-items: center;
  gap: 28px;
}

.timer-display {
  font-family: "Orbitron", sans-serif;
  font-size: clamp(3rem, 7vw, 6rem);
  font-weight: 700;
  letter-spacing: 4px;
  color: #fff;
  min-width: 220px;
  text-align: center;
  transition: color 0.3s;
  line-height: 1;
}
.timer-display.warning {
  color: #fbbf24;
}
.timer-display.danger {
  color: #f87171;
  animation: tblink 0.8s infinite;
}
@keyframes tblink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.25;
  }
}

.timer-track {
  flex: 1;
  height: 14px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 7px;
  overflow: hidden;
}
.timer-fill {
  height: 100%;
  border-radius: 7px;
  background: #22c55e;
  transition: width 0.4s linear, background 0.4s;
}
.timer-fill.warning {
  background: #fbbf24;
}
.timer-fill.danger {
  background: #ef4444;
}

/* Overlay ganador */
.winner-overlay {
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.winner-overlay.rojo {
  background: rgba(120, 0, 0, 0.94);
}
.winner-overlay.azul {
  background: rgba(0, 20, 100, 0.94);
}

.winner-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    circle at center,
    rgba(243, 188, 44, 0.45) 0%,
    transparent 65%
  );
  animation: glow-pulse 1.2s ease-in-out infinite alternate;
}
@keyframes glow-pulse {
  from {
    opacity: 0.4;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1.08);
  }
}

.winner-inner {
  position: relative;
  z-index: 1;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.winner-trophy {
  font-size: clamp(4rem, 9vw, 8rem);
  filter: drop-shadow(0 0 30px rgba(243, 188, 44, 0.9));
  animation: trophy-in 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
@keyframes trophy-in {
  from {
    opacity: 0;
    transform: scale(0) rotate(-20deg);
  }
  to {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
}
.winner-label {
  font-family: "Orbitron", sans-serif;
  font-size: clamp(0.8rem, 1.5vw, 1.2rem);
  letter-spacing: 10px;
  color: #f3bc2c;
  animation: fade-up 0.4s 0.2s ease both;
}
.winner-name {
  font-size: clamp(2.5rem, 6vw, 5.5rem);
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
  text-shadow: 0 2px 30px rgba(0, 0, 0, 0.7);
  animation: fade-up 0.4s 0.35s ease both;
}
@keyframes fade-up {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Confetti */
.confetti-wrap {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.conf {
  position: absolute;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  top: 5%;
  left: calc(var(--i) * 7.14%);
  background: hsl(calc(var(--i) * 26), 90%, 60%);
  animation: conf-fall 1.8s calc(var(--i) * 0.12s) ease-in infinite;
}
@keyframes conf-fall {
  0% {
    opacity: 1;
    transform: translateY(-10px) rotate(0deg);
  }
  100% {
    opacity: 0;
    transform: translateY(100vh) rotate(480deg);
  }
}

.winner-pop-enter-active {
  transition: all 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.winner-pop-leave-active {
  transition: all 0.35s ease;
}
.winner-pop-enter-from {
  opacity: 0;
  transform: scale(0.85);
}
.winner-pop-leave-to {
  opacity: 0;
  transform: scale(1.05);
}
</style>
